/**
 * PATH: packages/email-hub/src/feature/compose/useUndoSend.ts
 * FILE_ID: EMAIL-COMPOSE-HOOK-UNDOSEND
 * PURPOSE: React binding for the send-hold controller (sendHold.ts).
 * WHY: Gives ComposePage a tiny, declarative API ({ pending, secondsLeft,
 *      arm, cancel, sendNow }) while the timing/state machine stays in the
 *      tested, framework-agnostic controller.
 * FLOW: arm(commit) → countdown (secondsLeft ticks) → commit() | cancel()
 *       | sendNow() (skip the wait, fire immediately).
 *       On unmount a still-pending hold is flushed so a queued send is never
 *       silently dropped when the dock/page closes.
 * DEPENDENCIES:
 *   - react: useRef/useEffect/useSyncExternalStore
 *   - ./sendHold: createSendHold controller
 * RELATED:
 *   - pages/ComposePage.tsx: consumer.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'

import { createSendHold, DEFAULT_UNDO_WINDOW_MS, type SendHold } from './sendHold'

export interface UseUndoSend {
  pending: boolean
  secondsLeft: number
  arm: (commit: () => void) => void
  cancel: () => void
  /** Skip the remaining grace window and fire the queued send immediately
   *  (the composer's "Send now" button). No-op when nothing is pending. */
  sendNow: () => void
}

export function useUndoSend(windowMs: number = DEFAULT_UNDO_WINDOW_MS): UseUndoSend {
  // One controller per mounted composer; created lazily, never recreated.
  const holdRef = useRef<SendHold | null>(null)
  if (holdRef.current == null) holdRef.current = createSendHold(windowMs)
  const hold = holdRef.current

  const snapshot = useSyncExternalStore(hold.subscribe, hold.getSnapshot, hold.getSnapshot)

  // Don't lose a queued send if the composer unmounts mid-window — fire it.
  useEffect(() => () => hold.flush(), [hold])

  return {
    pending: snapshot.pending,
    secondsLeft: snapshot.secondsLeft,
    arm: hold.arm,
    cancel: hold.cancel,
    sendNow: hold.flush,
  }
}
